import React, { useContext } from 'react';
import {
  forwardRef,
  useTab,
  Flex,
  TabProps,
  Spacer,
  Button,
  Box,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import {
  getTabsShowMoreMenuStyles,
  getTabShowMoreMenuChildrenStyle,
} from './tabs-showmore-menu-theme';
import { TabsContext } from './tabs';
import { TabBadge } from './tabs-badge';
import { ChevronDownIcon } from '@allergan-data-labs/alle-elements-icons/20/chevronDownIcon';
import {
  ActionListItem,
  ActionMenu,
  ActionMenuContent,
  ActionMenuGroup,
} from '@allergan-data-labs/alle-elements-action-menu';

interface TabsShowMoreMenuProps extends TabProps {
  children?: React.ReactNode;
  tabs?: {
    tab: React.ReactElement;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  }[];
  colorMode?: ColorMode;
  selectedMenuItemRef?: React.RefObject<HTMLElement | null>;
}

export const TabsShowMoreMenu = forwardRef<TabsShowMoreMenuProps, 'button'>(
  (
    {
      tabs = [],
      colorMode: propColorMode,
      selectedMenuItemRef,
    }: TabsShowMoreMenuProps,
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    const tabProps = useTab({ ref });

    const { ref: tabRef, onClick: tabOnClick, ...menuItemTabProps } = tabProps;

    const styles = useMultiStyleConfig('Tabs');

    const { isFitted } = useContext(TabsContext);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
      }
    };

    return (
      <ActionMenu
        triggerElement={Button}
        triggerText="More"
        triggerElementProps={{
          __css: styles.tab,
          sx: getTabsShowMoreMenuStyles(
            selectedMenuItemRef,
            colorMode,
            isFitted
          ),
          rightIcon: <ChevronDownIcon />,
          ref: tabRef,
          ...menuItemTabProps,
        }}
      >
        <ActionMenuContent>
          <Box onKeyDown={handleKeyDown}>
            <ActionMenuGroup>
              {tabs.map(({ tab, onClick }, index) => (
                <ActionListItem
                  {...menuItemTabProps}
                  onClick={onClick}
                  key={tab.props.index}
                >
                  <Flex sx={getTabShowMoreMenuChildrenStyle()}>
                    {tab.props.children}
                    {tab.props.showBadge && (
                      <>
                        <Spacer />
                        <TabBadge
                          badgeIndex={index + 1 || 0}
                          colorMode={colorMode}
                        />
                      </>
                    )}
                  </Flex>
                </ActionListItem>
              ))}
            </ActionMenuGroup>
          </Box>
        </ActionMenuContent>
      </ActionMenu>
    );
  }
);
